import Typography from '@ds/components/typography/Typography';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode, useLayoutEffect, useRef } from 'react';

import styles from './Footer.module.css';

/** Owner/ABN line shown in the footer, defaulting to the current calendar year. */
const defaultCompanyLine = (year: number): string =>
    `Integrity Systems Company Limited (ISC Ltd) ABN 34 134 745 038 © ${year}`;

/** The custom property on <html> that Footer.module.css reads for the page's scroll-padding-bottom. */
const STRAP_HEIGHT_PROPERTY = '--footer-strap-height';

/**
 * Publishes the strap's rendered height on <html> while it is mounted, so the page's scroll-padding keeps a
 * focused field clear of it (WCAG 2.4.11, ENVDB-9286 item 81). The strap is 112px at 100% but grows when its
 * buttons wrap or the text is enlarged, so it is measured rather than assumed. CSS decides when the value applies.
 */
const usePublishStrapHeight = (strapRef: React.RefObject<HTMLElement>, hasStrap: boolean): void => {
    useLayoutEffect(() => {
        const strap = strapRef.current;
        if (!hasStrap || !strap) return undefined;

        const root = document.documentElement;
        const publish = (): void => {
            root.style.setProperty(STRAP_HEIGHT_PROPERTY, `${Math.ceil(strap.getBoundingClientRect().height)}px`);
        };

        publish();
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(publish);
        observer?.observe(strap);

        return () => {
            observer?.disconnect();
            root.style.removeProperty(STRAP_HEIGHT_PROPERTY);
        };
    }, [strapRef, hasStrap]);
};

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
    /**
     * Optional action / navigation bar rendered as a pale-blue (`--bg-info`) band that sticks to
     * the bottom of the viewport (`position: sticky`) while the page scrolls, so
     * it stays visible without waiting for the end of the content. Scrolling past
     * the page content releases it, revealing the copyright bar beneath. Pass the
     * page's navigation buttons here (e.g. Previous / Next, Back to summary /
     * Submit) to render the "with nav" variant from the design.
     *
     * The bar lays its content out as a space-between row, so wrap left- and
     * right-aligned groups in their own containers (see the Storybook examples).
     * When omitted, only the footer bar renders.
     *
     * While the band is sticky, the page's `scroll-padding-bottom` follows its
     * measured height so a focused field is never hidden behind it. Below 600px
     * wide or tall it is not sticky and sits after the page content.
     */
    actions?: ReactNode;
    /**
     * Owner / ABN line shown at the top of the footer.
     * @default `Integrity Systems Company Limited (ISC Ltd) ABN 34 134 745 038 © <current year>`
     */
    companyLine?: ReactNode;
    /**
     * Copyright line shown beneath the separator.
     * @default `Copyright © <current year>`
     */
    copyright?: ReactNode;
    /** Additional class names merged onto the `<footer>` element. */
    className?: string;
}

/**
 * Page footer for the eNVD app. Renders the dark owner/copyright bar, and —
 * when `actions` are supplied — a pale-blue navigation band above it (the
 * "with nav" variant in the design system).
 *
 * Layout is composed from the `Typography` primitive and a plain rule; the action
 * bar is a slot so page-specific navigation lives with the consumer rather than
 * inside the footer.
 *
 * @example
 * // Default — owner + copyright only
 * <Footer />
 *
 * @example
 * // With navigation band above the footer
 * <Footer
 *     actions={
 *         <>
 *             <Flex gap={3}>
 *                 <Button variant="secondary" transparent size="lg" startIcon={<KeyboardArrowLeftIcon />}>Previous page</Button>
 *                 <Button variant="primary" size="lg" endIcon={<KeyboardArrowRightIcon />}>Next step</Button>
 *             </Flex>
 *             <Flex gap={3}>
 *                 <Button variant="text" size="lg" startIcon={<HomeIcon />}>Back to summary page</Button>
 *                 <Button variant="secondary" transparent size="lg" endIcon={<HowToVoteIcon />}>Submit</Button>
 *             </Flex>
 *         </>
 *     }
 * />
 */
const Footer = forwardRef<HTMLElement, FooterProps>(({ actions, companyLine, copyright, className, ...rest }, ref) => {
    const year = new Date().getFullYear();
    const company = companyLine ?? defaultCompanyLine(year);
    const copyrightLine = copyright ?? `Copyright © ${year}`;
    const strapRef = useRef<HTMLDivElement>(null);
    usePublishStrapHeight(strapRef, Boolean(actions));

    return (
        <>
            {actions && (
                <div
                    ref={strapRef}
                    className={styles.actionBar}
                    data-footer-actions
                >
                    {/* A landmark of its own, outside the <footer> (contentinfo): the strap is the form's
                        navigation, not site information. It sits beside <main> rather than inside it because it
                        is full-bleed and sticky; its icons are decorative by default (ENVDB-9286 item 82). */}
                    <nav
                        className={styles.actionBarInner}
                        aria-label="Form steps"
                    >
                        {actions}
                    </nav>
                </div>
            )}
            <footer
                ref={ref}
                className={clsx(styles.footer, className)}
                {...rest}
            >
                <div className={styles.inner}>
                    <Typography
                        variant="body-1"
                        color="inverse"
                    >
                        {company}
                    </Typography>
                    <hr className={styles.rule} />
                    <div className={styles.copyright}>
                        <Typography
                            variant="body-2"
                            color="inverse"
                        >
                            {copyrightLine}
                        </Typography>
                    </div>
                </div>
            </footer>
        </>
    );
});

Footer.displayName = 'Footer';

export default Footer;
